import React from 'react';
import { api } from '@services/apiRequest';
import { getDateStr } from '@pages/Planner/Calendar/Calendar.lib';
import { SelectedLevel, SelectedProvince, TextFilter } from '@pages/Games/Games.type';

export interface PaginatedGamesWithDocuments {
  total: number;
  num_missing: number;
  items: any[];
}

/**
 * @function getDocumentsData
 *
 * Get games data for documents management with count of missing documents
 *
 * @param {Date} startDate
 * @param {Date} endDate
 * @param textFilters
 * @param {SelectedProvince[]} selectedProvinces
 * @param {SelectedLevel[]} selectedLevels
 * @param {boolean} showMissing
 * @param {boolean} showUploaded
 * @param {boolean} includeSecondRef
 * @param {any} push
 * @param {number} limit
 * @param {number} offset
 * @param {React.Dispatch<PaginatedGamesWithDocuments | undefined>} setData
 */
export const getDocumentsData = async (
  startDate: Date,
  endDate: Date,
  textFilters: TextFilter,
  selectedProvinces: SelectedProvince[],
  selectedLevels: SelectedLevel[],
  showMissing: boolean,
  showUploaded: boolean,
  includeSecondRef: boolean,
  push: any,
  limit?: number,
  offset?: number,
  setData?: React.Dispatch<PaginatedGamesWithDocuments | undefined>
) => {
  let searchParams = {
    organizer: 'CR MARCHE',
    ...(startDate && { start_date: getDateStr(startDate) }),
    ...(endDate && { end_date: getDateStr(endDate) }),
    ...(textFilters.member && { member: textFilters.member }),
    ...(textFilters.game_number && { game_number: textFilters.game_number }),
    ...(selectedProvinces.length && { provinces: JSON.stringify(selectedProvinces.map((i) => i.value)) }),
    ...(selectedLevels.length && { levels: JSON.stringify(selectedLevels.map((i) => i.value)) }),
    show_missing: showMissing.toString(),
    show_uploaded: showUploaded.toString(),
    ...(includeSecondRef && { include_second_ref: includeSecondRef.toString() }),
  };
  
  if (limit) {
    searchParams = { ...searchParams, ...{ limit: limit.toString() } };
  }
  if (offset !== undefined) {
    searchParams = { ...searchParams, ...{ offset: offset.toString() } };
  }

  try {
    const { data } = await api.get(`/games/with-documents?${new URLSearchParams(searchParams)}`);
    if (setData) {
      setData(data);
    } else {
      return data;
    }
  } catch (e) {
    push({ title: 'Errore del server', type: 'error' });
  }
};

/**
 * @function uploadDocument
 * Upload one or multiple PDF documents for a game
 */
export const uploadDocument = async (codGame: string, files: File[], push: any) => {
  // Validate total file size (max 5MB)
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
  const totalSize = files.reduce((acc, file) => acc + file.size, 0);
  
  if (totalSize > MAX_FILE_SIZE) {
    const fileSizeMB = (totalSize / (1024 * 1024)).toFixed(2);
    push({ 
      title: `File troppo grande (${fileSizeMB} MB). Dimensione massima consentita: 5 MB`, 
      type: 'error' 
    });
    throw new Error('File too large');
  }

  const formData = new FormData();
  files.forEach(file => {
    formData.append('files', file);
  });

  try {
    const { data } = await api.post(`/games/upload-document/${codGame}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    const message = files.length > 1 
      ? `${files.length} documenti caricati e uniti con successo`
      : 'Referto caricato con successo';
    push({ title: message, type: 'success' });
    return data;
  } catch (e: any) {
    const errorMessage = e?.response?.data?.detail || 'Errore durante il caricamento del referto';
    push({ title: errorMessage, type: 'error' });
    throw e;
  }
};

/**
 * @function downloadDocument
 * Get a presigned URL to download a game document
 */
export const downloadDocument = async (codGame: string, push: any) => {
  try {
    const { data } = await api.get(`/games/download-document/${codGame}`);
    // Create a temporary link element to trigger download
    const link = document.createElement('a');
    link.href = data.url;
    link.download = `${codGame}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (e: any) {
    const errorMessage = e?.response?.data?.detail || 'Errore durante il download del referto';
    push({ title: errorMessage, type: 'error' });
  }
};

/**
 * @function deleteDocument
 * Delete a game document from S3 and update the database
 */
export const deleteDocument = async (codGame: string, push: any) => {
  try {
    await api.delete(`/games/delete-document/${codGame}`);
    push({ title: 'Referto eliminato con successo', type: 'success' });
  } catch (e: any) {
    const errorMessage = e?.response?.data?.detail || 'Errore durante l\'eliminazione del referto';
    push({ title: errorMessage, type: 'error' });
    throw e;
  }
};

/**
 * @function markDocumentAtOffice
 * Mark a game document as already present at the office (sets num_documents_warnings to -1)
 */
export const markDocumentAtOffice = async (codGame: string, push: any) => {
  try {
    await api.post(`/games/mark-document-at-office/${codGame}`);
    push({ title: 'Referto segnato come già presente negli uffici', type: 'success' });
  } catch (e: any) {
    const errorMessage = e?.response?.data?.detail || 'Errore durante l\'aggiornamento del referto';
    push({ title: errorMessage, type: 'error' });
    throw e;
  }
};

/**
 * @function reportDocumentAnomaly
 * Report an anomaly on a game document
 */
export const reportDocumentAnomaly = async (codGame: string, desAnomaly: string, push: any) => {
  try {
    await api.post(`/games/report-document-anomaly/${codGame}`, {
      des_anomaly: desAnomaly
    });
    push({ title: 'Anomalia segnalata con successo. Email inviata all\'arbitro.', type: 'success' });
  } catch (e: any) {
    const errorMessage = e?.response?.data?.detail || 'Errore durante la segnalazione dell\'anomalia';
    push({ title: errorMessage, type: 'error' });
    throw e;
  }
};

/**
 * @function validateDocument
 * Validate a game document after anomaly was fixed
 */
export const validateDocument = async (codGame: string, push: any) => {
  try {
    await api.post(`/games/validate-document/${codGame}`);
    push({ title: 'Referto validato con successo', type: 'success' });
  } catch (e: any) {
    const errorMessage = e?.response?.data?.detail || 'Errore durante la validazione del referto';
    push({ title: errorMessage, type: 'error' });
    throw e;
  }
};
